import {useCallback, useEffect, useRef} from "react";

export const DOUBLE_CLICK_DELAY = 250;

export function useRowClick<T extends Record<string, any>>(options: {
    idKey?: keyof T | string;
    getRowKey?: (row: T) => string | number | undefined;
    onSelect?: (row: T) => void;
    onSingle?: (row: T) => void;
    onDouble?: (row: T) => void;
    delay?: number;
} = {}) {
    const {idKey, getRowKey, onSingle, onSelect, onDouble, delay = DOUBLE_CLICK_DELAY} = options;

    const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastRowKeyRef = useRef<string | number | null>(null);

    const effectiveGetRowKey = useCallback(
        (row: T): string | number | undefined => {
            if (!row) return undefined;
            if (typeof getRowKey === "function") return getRowKey(row);
            if (idKey != null) return (row as any)[idKey as string];
            return (row as any).id ?? (row as any)["id"];
        },
        [getRowKey, idKey]
    );

    const handleRowClick = useCallback(
        (row: T) => {
            if (!row) return;

            const rowKey = effectiveGetRowKey(row);
            if (rowKey == null) return;

            if (clickTimeoutRef.current && lastRowKeyRef.current === rowKey) {
                clearTimeout(clickTimeoutRef.current);
                clickTimeoutRef.current = null;
                lastRowKeyRef.current = null;
                if (typeof onSelect === "function") onSelect(row);
                if (typeof onDouble === "function") onDouble(row);
                return;
            }

            if (clickTimeoutRef.current && lastRowKeyRef.current !== rowKey) {
                clearTimeout(clickTimeoutRef.current);
                clickTimeoutRef.current = null;
            }

            if (typeof onSelect === "function") onSelect(row);

            lastRowKeyRef.current = rowKey;

            clickTimeoutRef.current = setTimeout(() => {
                if (typeof onSingle === "function") onSingle(row);
                clickTimeoutRef.current = null;
                lastRowKeyRef.current = null;
            }, delay);
        },
        [effectiveGetRowKey, onSelect, onSingle, onDouble, delay]
    );

    useEffect(() => {
        return () => {
            if (clickTimeoutRef.current) {
                clearTimeout(clickTimeoutRef.current);
                clickTimeoutRef.current = null;
            }
        };
    }, []);

    return handleRowClick;
}
